/* eslint-disable react-hooks/exhaustive-deps */
/**
 *
 * SubjectForm
 *
 */
import React, { memo } from 'react';
import { Field, Form, Formik, FormikProps, FieldArray } from 'formik';
import * as Yup from 'yup';
import { Input } from 'core/components/Input/Loadable';
import { Button } from 'core/components/Button';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { useBranchSetupSlice } from 'features/BranchSetup/slice';
import { RootState } from 'controller/types';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isRequiredString } from 'core/utils/validationSchemas';
import { SetupLoader } from 'core/components/SetupComponents/SetupLoader';

interface Props {
  values?: any;
  action?: string;
  onNext?: () => void;
}

const subjectSchema = Yup.object({
  SUBJECT_NAME: isRequiredString('Subject Name is a required field.'),
});

const validationSchema = () =>
  Yup.object().shape({
    Subjectarray: Yup.array()
      .of(subjectSchema)
      .test('unique-subjects', 'Duplicate subject names are not allowed', function(value) {
        if (!value) return true;
        
        const subjectNames = value
          .map(item => item.SUBJECT_NAME?.trim().toLowerCase())
          .filter(name => name); // Remove empty values
        
        const uniqueNames = new Set(subjectNames);
        
        if (subjectNames.length !== uniqueNames.size) {
          // Find duplicate names and their indices
          const seen = new Map();
          const duplicateIndices: number[] = [];
          
          value.forEach((item, index) => {
            const normalizedName = item.SUBJECT_NAME?.trim().toLowerCase();
            if (normalizedName) {
              if (seen.has(normalizedName)) {
                duplicateIndices.push(index);
                if (!duplicateIndices.includes(seen.get(normalizedName))) {
                  duplicateIndices.push(seen.get(normalizedName));
                }
              } else {
                seen.set(normalizedName, index);
              }
            }
          });
          
          // Create error for the first duplicate found
          if (duplicateIndices.length > 0) {
            const firstDuplicateIndex = duplicateIndices[0];
            return this.createError({
              path: `${this.path}[${firstDuplicateIndex}].SUBJECT_NAME`,
              message: 'This subject name already exists',
            });
          }
        }
        
        return true;
      }),
  });

export const SubjectForm = memo((props: Props) => {
  const dispatch = useDispatch();
  const state = useSelector((state: RootState) => state.branchSetup);
  const { actions } = useBranchSetupSlice();
  const params: any = useParams();
  const authData = useAuth();

  const [subjectCounter, setSubjectCounter] = React.useState<number>(1);
  const [initialValues, setInitialValues] = React.useState<any>({});

  const defaultData = () => {
    return [
      {
        BRANCH_ID: authData?.BRANCH_ID,
        SUBJECT_NAME: '',
      },
    ];
  };

  React.useEffect(() => {
    if (state?.navigate) {
      props?.onNext && props?.onNext();
      dispatch(actions.setNavigate(false));
    }
  }, [state?.navigate]);

  React.useEffect(() => {
    if (props?.values?.SUBJECT_ID) {
      const { STATUS, ...reset } = props?.values;
      setInitialValues({
        Subjectarray: [reset],
      });
    } else {
      setInitialValues({
        Subjectarray: defaultData(),
      });
    }
  }, [props?.values?.SUBJECT_ID]);

  return !!initialValues?.Subjectarray?.[0]?.BRANCH_ID ? (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema()}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={async values => {
        dispatch(
          actions.createBranchSetupStart({
            URL: (params?.url as string) || 'subject',
            data: values.Subjectarray,
            action: props?.action || 'save',
            message: props?.action
              ? 'Updated Successfully!'
              : 'Created Successfully!',
          }),
        );
      }}
    >
      {(formikProps: FormikProps<any>) => (
        <Form>
          <div>
            <FieldArray name="Subjectarray">
              {({ push, remove }) => (
                <>
                  {formikProps?.values?.Subjectarray?.map((x, i) => (
                    <div
                      key={'FieldArrayOfSubjectarray' + i}
                      className="py-4 px-2"
                    >
                      <h1 className="font-bold text-xl border-b-[1px] border-b-neutral-50">
                        Subject: {i + 1}
                      </h1>
                      <div className="grid grid-cols-2 items-start gap-4 mt-4">
                        <Field name={`Subjectarray.${i}.SUBJECT_NAME`}>
                          {({ field, form, meta }) => (
                            <Input
                              type="text"
                              label="Subject Name"
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Enter Subject Name"
                              name={field.name}
                              error={meta.touched && meta.error}
                              message={meta.touched && meta.error}
                              required
                              maxLength={100}
                            />
                          )}
                        </Field>
                      </div>
                    </div>
                  ))}
                  {props?.action !== 'update' && (
                    <div className="flex justify-end mt-5">
                      {subjectCounter > 1 && (
                        <p
                          className="inline text-slate-500 my-5 text-lg font-bold cursor-pointer"
                          onClick={() => {
                            remove(subjectCounter - 1);
                            setSubjectCounter(counter => counter - 1);
                          }}
                        >
                          Remove
                        </p>
                      )}
                      <p
                        className="inline text-primary-main ml-5 my-5 text-lg font-bold cursor-pointer"
                        onClick={() => {
                          setSubjectCounter(counter => counter + 1);
                          push(defaultData()[0]);
                        }}
                      >
                        Add Subject
                      </p>
                    </div>
                  )}
                </>
              )}
            </FieldArray>
          </div>
          <div className="text-center">
            <Button
              submit
              type="primary"
              label="Submit"
              disabled={!formikProps.dirty || !formikProps.isValid}
            />
          </div>
        </Form>
      )}
    </Formik>
  ) : (
    <SetupLoader />
  );
});